import React, { useState, useEffect, useCallback } from 'react';

// janela_dia semântica:
//   < 0  → bloquear vagas que começam nas próximas |janela_dia| horas (antecedência)
//   = 0  → só vagas de agora em diante
//   > 0  → incluir vagas que iniciaram há até janela_dia horas
const DEFAULT_VIS = {
  janela_dia: 1,
  semanas_anteriores: 0,
  semanas_futuras: 8,
  exibir_vagas_ocupadas: true
};

const Toggle = ({ label, description, value, onChange }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
    <div>
      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1e293b' }}>{label}</div>
      {description && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>{description}</div>}
    </div>
    <button onClick={() => onChange(!value)} style={{
      width: '46px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
      background: value ? 'var(--color-primary, #0f5a30)' : '#cbd5e1',
      position: 'relative', transition: 'all 0.2s ease', flexShrink: 0
    }}>
      <span style={{
        position: 'absolute', top: '3px', width: '20px', height: '20px', borderRadius: '50%',
        background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        left: value ? '23px' : '3px', transition: 'left 0.2s ease'
      }} />
    </button>
  </div>
);

const OptionButtons = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
    {options.map(opt => (
      <button key={opt.value} onClick={() => onChange(opt.value)} style={{
        padding: '8px 16px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
        border: '2px solid', cursor: 'pointer', transition: 'all 0.15s',
        borderColor: value === opt.value ? 'var(--color-primary, #0f5a30)' : '#e2e8f0',
        background: value === opt.value ? 'var(--color-primary, #0f5a30)' : 'white',
        color: value === opt.value ? 'white' : '#475569'
      }}>{opt.label}</button>
    ))}
  </div>
);

const InfoBox = ({ color = 'blue', children }) => {
  const map = {
    blue:   { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
    green:  { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
    yellow: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' }
  };
  const c = map[color];
  return (
    <div style={{ background: c.bg, borderRadius: '12px', padding: '14px 16px', border: `1px solid ${c.border}`, fontSize: '0.82rem', color: c.text }}>
      {children}
    </div>
  );
};

// Opções da escala unificada: negativo = bloquear futuro; zero = agora; positivo = incluir passado
const JANELA_OPCOES = [
  { value: -2, label: '−2h',  group: 'bloquear', title: 'Bloquear próximas 2h' },
  { value: -1, label: '−1h',  group: 'bloquear', title: 'Bloquear próxima 1h'  },
  { value:  0, label: 'Agora',group: 'neutro',   title: 'Só vagas de agora em diante' },
  { value:  1, label: '+1h',  group: 'passado',  title: 'Incluir vagas iniciadas há até 1h' },
  { value:  2, label: '+2h',  group: 'passado',  title: 'Incluir vagas iniciadas há até 2h' },
  { value:  4, label: '+4h',  group: 'passado',  title: 'Incluir vagas iniciadas há até 4h' },
  { value:  6, label: '+6h',  group: 'passado',  title: 'Incluir vagas iniciadas há até 6h' },
  { value: 24, label: '+24h', group: 'passado',  title: 'Incluir vagas iniciadas há até 24h' }
];

const groupColor = {
  bloquear: { active: '#b45309', border: '#fde68a', bg: '#fffbeb', text: '#92400e' },
  neutro:   { active: '#475569', border: '#cbd5e1', bg: '#f8fafc', text: '#334155' },
  passado:  { active: 'var(--color-primary,#0f5a30)', border: '#bbf7d0', bg: '#f0fdf4', text: '#166534' }
};

function descricaoJanela(v) {
  if (v < 0)  return `Vagas que começam nas próximas ${Math.abs(v)}h ficam ocultas — impede agendamentos de última hora`;
  if (v === 0) return 'Apenas vagas de agora em diante são exibidas';
  return `Vagas iniciadas há até ${v}h ainda aparecem na grade — permite cancelamentos tardios`;
}

export const GerenciadorVisualizacaoAgenda = ({ createApi, isMobile = false }) => {
  const [config, setConfig] = useState(DEFAULT_VIS);
  const [fullConfig, setFullConfig] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState(null);
  const [alterado, setAlterado] = useState(false);

  useEffect(() => {
    const api = createApi();
    api.get('/site/configuracoes')
      .then(res => {
        const saved = res.data.configuracoes || {};
        setFullConfig(saved);
        if (saved.visualizacao) {
          setConfig({ ...DEFAULT_VIS, ...saved.visualizacao });
        }
      })
      .catch(() => {})
      .finally(() => setCarregando(false));
  }, [createApi]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const atualizar = useCallback((campo, valor) => {
    setConfig(c => ({ ...c, [campo]: valor }));
    setAlterado(true);
  }, []);

  const salvar = async () => {
    setSalvando(true);
    try {
      const api = createApi();
      const novoFullConfig = { ...fullConfig, visualizacao: config };
      await api.put('/admin/site/configuracoes', { configuracoes: novoFullConfig });
      setFullConfig(novoFullConfig);
      setToast({ type: 'success', msg: '✅ Regras de visualização salvas com sucesso!' });
      setAlterado(false);
      window.dispatchEvent(new CustomEvent('siteConfigUpdate', { detail: novoFullConfig }));
    } catch {
      setToast({ type: 'error', msg: '❌ Erro ao salvar. Tente novamente.' });
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: 'var(--color-primary,#0f5a30)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  const janelaAtual = config.janela_dia ?? 1;
  const grupoAtual  = janelaAtual < 0 ? 'bloquear' : janelaAtual === 0 ? 'neutro' : 'passado';
  const gc = groupColor[grupoAtual];

  const semAntOpcoes = [
    { value: 0, label: 'Nenhuma' },
    { value: 1, label: '1 semana' },
    { value: 2, label: '2 semanas' },
    { value: 4, label: '4 semanas' }
  ];

  const semFutOpcoes = [
    { value: 1, label: '1 semana' },
    { value: 2, label: '2 semanas' },
    { value: 4, label: '4 semanas' },
    { value: 8, label: '8 semanas' },
    { value: 12, label: '12 semanas' },
    { value: 0, label: 'Ilimitado' }
  ];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", color: '#1e293b' }}>

      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          background: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${toast.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: toast.type === 'success' ? '#166534' : '#991b1b',
          padding: '12px 20px', borderRadius: '12px', fontWeight: 600,
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)', fontSize: '0.875rem'
        }}>{toast.msg}</div>
      )}

      <div style={{ padding: isMobile ? '16px' : '20px 28px', borderBottom: '1px solid #f1f5f9', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.05rem' : '1.15rem', fontWeight: 700 }}>👁️ Regras de Visualização da Agenda</h2>
          <p style={{ margin: '3px 0 0', fontSize: '0.8rem', color: '#64748b' }}>Controla o que os alunos veem e como podem navegar na agenda</p>
        </div>
        <button onClick={salvar} disabled={salvando || !alterado} style={{
          padding: '8px 18px', borderRadius: '10px', border: 'none',
          background: alterado ? 'var(--color-primary,#0f5a30)' : '#e2e8f0',
          color: alterado ? 'white' : '#94a3b8', fontSize: '0.85rem', fontWeight: 700,
          cursor: alterado ? 'pointer' : 'not-allowed', transition: 'all 0.2s ease'
        }}>
          {salvando ? 'Salvando...' : '💾 Salvar'}
        </button>
      </div>

      <div style={{ padding: isMobile ? '16px' : '24px 28px', display: 'flex', flexDirection: 'column', gap: '28px', maxWidth: '640px' }}>

        {/* ── Janela de visibilidade no dia ── */}
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '4px', color: '#1e293b' }}>
            🕐 Visibilidade de vagas no dia atual
          </div>
          <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.6 }}>
            Define quais vagas do dia são exibidas para os alunos em relação ao horário atual.
            Valores negativos <strong>escondem</strong> vagas próximas; positivos <strong>mostram</strong> vagas já iniciadas.
          </p>

          {/* Escala visual */}
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, borderRadius: '12px', overflow: 'hidden', border: '1.5px solid #e2e8f0', marginBottom: '12px' }}>
            {/* Label esquerdo */}
            <div style={{ background: '#fffbeb', padding: '6px 10px', display: 'flex', alignItems: 'center', borderRight: '1px solid #fde68a', flexShrink: 0 }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#92400e', textAlign: 'center', lineHeight: 1.2 }}>🚫 Bloquear<br/>próximas</span>
            </div>

            {/* Botões */}
            <div style={{ display: 'flex', flex: 1, overflowX: 'auto' }}>
              {JANELA_OPCOES.map((opt, i) => {
                const isSelected = janelaAtual === opt.value;
                const gc2 = groupColor[opt.group];
                const isNeutro = opt.group === 'neutro';
                return (
                  <button
                    key={opt.value}
                    title={opt.title}
                    onClick={() => atualizar('janela_dia', opt.value)}
                    style={{
                      flex: isNeutro ? '0 0 auto' : 1,
                      minWidth: isNeutro ? '64px' : '44px',
                      padding: '10px 6px',
                      border: 'none',
                      borderRight: i < JANELA_OPCOES.length - 1 ? '1px solid #e2e8f0' : 'none',
                      cursor: 'pointer',
                      fontSize: isNeutro ? '0.72rem' : '0.82rem',
                      fontWeight: isSelected ? 800 : 600,
                      transition: 'all 0.15s',
                      background: isSelected ? gc2.active : (isNeutro ? '#f8fafc' : opt.group === 'bloquear' ? '#fffbeb' : '#f0fdf4'),
                      color: isSelected ? 'white' : gc2.text,
                      outline: isSelected ? `2px solid ${gc2.active}` : 'none',
                      outlineOffset: '-2px'
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Label direito */}
            <div style={{ background: '#f0fdf4', padding: '6px 10px', display: 'flex', alignItems: 'center', borderLeft: '1px solid #bbf7d0', flexShrink: 0 }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#166534', textAlign: 'center', lineHeight: 1.2 }}>✅ Incluir<br/>passado</span>
            </div>
          </div>

          {/* Descrição do valor selecionado */}
          <InfoBox color={grupoAtual === 'bloquear' ? 'yellow' : grupoAtual === 'neutro' ? 'blue' : 'green'}>
            <strong>
              {janelaAtual < 0 && `−${Math.abs(janelaAtual)}h `}
              {janelaAtual === 0 && 'Agora '}
              {janelaAtual > 0 && `+${janelaAtual}h `}
            </strong>
            — {descricaoJanela(janelaAtual)}
          </InfoBox>
        </div>

        {/* ── Semanas anteriores ── */}
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '6px', color: '#1e293b' }}>
            ◀ Semanas anteriores visíveis para alunos
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>
            Quantas semanas para trás os alunos podem navegar a partir da semana atual.
          </p>
          <OptionButtons options={semAntOpcoes} value={config.semanas_anteriores} onChange={v => atualizar('semanas_anteriores', v)} />
          <div style={{ marginTop: '10px' }}>
            <InfoBox color={config.semanas_anteriores === 0 ? 'yellow' : 'green'}>
              {config.semanas_anteriores === 0
                ? <><strong>Sem semanas anteriores</strong> — alunos só veem a semana atual e as futuras</>
                : <><strong>{config.semanas_anteriores} semana{config.semanas_anteriores > 1 ? 's' : ''} atrás</strong> — alunos podem ver agendamentos passados</>}
            </InfoBox>
          </div>
        </div>

        {/* ── Semanas futuras ── */}
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '6px', color: '#1e293b' }}>
            ▶ Semanas futuras visíveis para alunos
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>
            Limite de semanas para frente que os alunos podem navegar.
          </p>
          <OptionButtons options={semFutOpcoes} value={config.semanas_futuras} onChange={v => atualizar('semanas_futuras', v)} />
          <div style={{ marginTop: '10px' }}>
            <InfoBox color={config.semanas_futuras === 0 ? 'yellow' : 'green'}>
              {config.semanas_futuras === 0
                ? <><strong>Ilimitado</strong> — alunos podem navegar para qualquer semana futura</>
                : <><strong>{config.semanas_futuras} semana{config.semanas_futuras > 1 ? 's' : ''} à frente</strong> — alunos não podem navegar além desse limite</>}
            </InfoBox>
          </div>
        </div>

        {/* ── Vagas ocupadas ── */}
        <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '4px 16px' }}>
          <Toggle
            label="Exibir vagas ocupadas para alunos"
            description="Quando desativado, vagas já agendadas por outro aluno ficam ocultas na grade pública"
            value={config.exibir_vagas_ocupadas}
            onChange={v => atualizar('exibir_vagas_ocupadas', v)}
          />
        </div>

        <InfoBox color="blue">
          <strong>ℹ️ Importante:</strong> Estas regras se aplicam apenas à visualização dos <strong>alunos</strong>. Administradores e professores sempre veem todas as vagas e podem navegar livremente entre semanas.
        </InfoBox>

      </div>
    </div>
  );
};
